import React, { useState } from 'react'
import { COMPANY_INFO } from '../lib/constants'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '', // Mandatory honeypot field
  })
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!res.ok) {
        throw new Error('Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte.')
      }

      setStatus('success')
      setFormData({
        navn: '',
        telefon: '',
        email: '',
        beskrivelse: '',
        website_url: '',
      })
    } catch (err) {
      console.error('Submission error:', err)
      setStatus('error')
      setErrorMessage(err.message || 'Der opstod en uventet fejl. Kontakt os venligst pr. telefon.')
    }
  }

  return (
    <div className="bg-[#FFFFFF] rounded-3xl border border-[#292A30]/20 shadow-md p-6 sm:p-10">
      {/* Primary Call Emphasis */}
      <div className="bg-[#18191D] text-white rounded-2xl p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-center justify-between gap-6 border border-white/10">
        <div className="text-center sm:text-left">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#86878D] mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
            <span>Hurtigste svartid</span>
          </span>
          <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            Ring direkte til Jakob
          </h3>
          <p className="text-[#86878D] text-xs sm:text-sm mt-1">
            Få hurtig rådgivning, pris og aftale direkte med den elektriker, der udfører arbejdet.
          </p>
        </div>

        <a
          href={`tel:${COMPANY_INFO.phoneRaw}`}
          className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#E36C0A] hover:bg-[#B95000] text-white font-bold px-6 py-3.5 shadow-md transition-all text-xs uppercase tracking-wider whitespace-nowrap shrink-0"
        >
          <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
          <span>{COMPANY_INFO.phone}</span>
        </a>
      </div>

      <div className="border-t border-[#292A30]/10 pt-6">
        <h4 className="text-lg font-bold text-[#18191D] mb-1">
          Eller send en skriftlig forespørgsel
        </h4>
        <p className="text-xs sm:text-sm text-[#55565C] mb-6">
          Udfyld formularen nedenfor, så vender Jakob tilbage hurtigst muligt.
        </p>

        {status === 'success' ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto text-xl">
              <i className="fa-solid fa-check" aria-hidden="true" />
            </div>
            <h5 className="text-base font-bold text-emerald-950">Tak for din henvendelse!</h5>
            <p className="text-sm text-emerald-800">
              Jakob har modtaget din besked og vender tilbage snarest. Haster opgaven, er du altid velkommen til at ringe på {COMPANY_INFO.phone}.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* MANDATORY ANTISPAM HONEYPOT - ABSOLUTELY HIDDEN */}
            <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
              <label htmlFor="website_url">Website URL (leave blank)</label>
              <input
                type="text"
                id="website_url"
                name="website_url"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website_url}
                onChange={handleChange}
              />
            </div>

            {status === 'error' && (
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-xs sm:text-sm text-rose-800 flex items-start gap-2.5">
                <i className="fa-solid fa-circle-exclamation text-rose-500 mt-0.5 shrink-0" aria-hidden="true" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="navn" className="block text-xs font-bold uppercase tracking-wider text-[#18191D] mb-1.5">
                  Dit navn <span className="text-[#E36C0A]">*</span>
                </label>
                <input
                  type="text"
                  id="navn"
                  name="navn"
                  required
                  value={formData.navn}
                  onChange={handleChange}
                  placeholder="Fx Lars Jensen"
                  className="w-full px-4 py-3 rounded-xl border border-[#292A30]/20 text-[#18191D] text-sm focus:outline-none focus:ring-2 focus:ring-[#E36C0A] focus:border-[#E36C0A] transition-all bg-[#F2F2F0]"
                />
              </div>

              <div>
                <label htmlFor="telefon" className="block text-xs font-bold uppercase tracking-wider text-[#18191D] mb-1.5">
                  Telefonnummer <span className="text-[#E36C0A]">*</span>
                </label>
                <input
                  type="tel"
                  id="telefon"
                  name="telefon"
                  required
                  value={formData.telefon}
                  onChange={handleChange}
                  placeholder="Fx 20 12 34 56"
                  className="w-full px-4 py-3 rounded-xl border border-[#292A30]/20 text-[#18191D] text-sm focus:outline-none focus:ring-2 focus:ring-[#E36C0A] focus:border-[#E36C0A] transition-all bg-[#F2F2F0]"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-[#18191D] mb-1.5">
                E-mailadresse <span className="text-[#E36C0A]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="Fx lars@eksempel.dk"
                className="w-full px-4 py-3 rounded-xl border border-[#292A30]/20 text-[#18191D] text-sm focus:outline-none focus:ring-2 focus:ring-[#E36C0A] focus:border-[#E36C0A] transition-all bg-[#F2F2F0]"
              />
            </div>

            <div>
              <label htmlFor="beskrivelse" className="block text-xs font-bold uppercase tracking-wider text-[#18191D] mb-1.5">
                Beskrivelse af opgaven <span className="text-[#E36C0A]">*</span>
              </label>
              <textarea
                id="beskrivelse"
                name="beskrivelse"
                required
                rows={4}
                value={formData.beskrivelse}
                onChange={handleChange}
                placeholder="Beskriv hvad du har brug for hjælp til (fx almindeligt el-arbejde, liftopgave, langt kabeltræk eller designerlampe)..."
                className="w-full px-4 py-3 rounded-xl border border-[#292A30]/20 text-[#18191D] text-sm focus:outline-none focus:ring-2 focus:ring-[#E36C0A] focus:border-[#E36C0A] transition-all bg-[#F2F2F0] resize-y"
              />
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#18191D] hover:bg-[#292A30] text-white font-bold px-7 py-3.5 shadow transition-colors text-xs uppercase tracking-wider disabled:opacity-50"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin" aria-hidden="true" />
                    <span>Sender forespørgsel...</span>
                  </>
                ) : (
                  <>
                    <span>Send forespørgsel</span>
                    <span aria-hidden="true">↗</span>
                  </>
                )}
              </button>

              <span className="text-xs text-[#55565C] text-center sm:text-right">
                Oplysninger bruges kun til at besvare din henvendelse.
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
